import {createSlice, createAsyncThunk, createSelector} from "@reduxjs/toolkit"
import axios from "axios"
import { fetchArticleBreadcrumbs } from "./breadcrumbSlice"
import cloneDeep from "lodash/cloneDeep"
import { selectAllowedNoteIds, selectIsLoading } from "./noteRestrictionsSlice"
import {detachNoteFromAttachment} from "./noteAttachmentsSlice"
import {NOTE_ACTIONS} from "./noteActionSlice"
import {pushNoteToOrder, setLoadedChildNotesOrder} from "./noteOrderSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"
//import {detachNoteFromAttachment} from "./noteAttachmentsSlice"

const defaultNoteObjectProperties = {
	order: -1,
	childCount: 0,
	noteChildren: {},
	showChildren: false,
	uniqueCount: 0,
	attachmentCount: 0,
	attachmentNoteCount: 0,
	articleNoteCount: 0,
}

const noteArrayToFlatObject = (notes) => {
	return notes.reduce((acc, curr, i) => {
		return {
			...acc,
			[curr.id]: {
				...curr,
				noteChildren: {},
				showChildren: false,
				order: i
			}
		}
	}, {})
}

const closeAllChildren = (notes) => {
	const queue = []
	// queue all top level notes

	// Start with top-level nodes
	for (const key in notes) {
		queue.push({ node: notes[key] })
	}

	// Iterate over the queue
	while (queue.length > 0) {
		const { node } = queue.shift()
		// Update the path
		node.showChildren = false
		// Enqueue children notes
		for (const key in node.noteChildren) {
			queue.push({ node: node.noteChildren[key]})
		}
	}
}

const updateNestedPaths = (notes, newTopLevelPath) => {
	// Queue for breadth-first traversal
	const queue = []

	// Start with top-level nodes
	for (const key in notes) {
		queue.push({ node: notes[key], newPath: `${newTopLevelPath}.${key}` })
	}

	// Iterate over the queue
	while (queue.length > 0) {
		const { node, newPath } = queue.shift()
		// Update the path
		node.path = newPath

		// Enqueue children notes
		for (const key in node.noteChildren) {
			queue.push({ node: node.noteChildren[key], newPath: `${newPath}.${key}` })
		}
	}
}

export const getClosestParentNoteByPath = (notes, path, excludeTerminalNode = false) => {
	// Split the path to navigate through the nested structure
	const keys = path.split(".")
	// remove terminal note key
	if (excludeTerminalNode) {
		keys.pop()
	}
	// Reference to the current point in the notes object
	let current = notes
	let lastValidNote = undefined // Store the last valid note

	for (const key of keys) {
		if (current[key]) {
			// If the key exists, update lastValidNote and move to the next level
			lastValidNote = current[key]
			current = current[key].noteChildren || current[key]
		} else {
			// If the key doesn't exist, break the loop and return the last valid note
			break
		}
	}
	return lastValidNote // Return the last valid note found
}

export const getNoteByPath = (notes, path) => {
	// Split the path to navigate through the nested structure
	const keys = path.split(".")
	
	// Reference to the current point in the notes object
	let current = notes

	for (let nodeLevel = 0; nodeLevel < keys.length; nodeLevel++) {
		//guard for cases where the note hasn't been loaded into workspace
		if (!current) return undefined
		if (nodeLevel === keys.length - 1) {
			// Last key, return this note for updating
			return current[keys[nodeLevel]]
		}
		// Navigate to the next level
		current = current[keys[nodeLevel]]?.noteChildren || current[keys[nodeLevel]]
	}

	return undefined
}

// Helper function to remove a note by path
const removeNoteByPath = (notes, path) => {
	const keys = path.split(".")
	let parent = undefined
	let current = notes

	for (let i = 0; i < keys.length - 1; i++) {
		// if note not loaded in, don't keep looking.
		if (!current || !current[keys[i]]) return null
		parent = current[keys[i]]	
		current = current[keys[i]]?.noteChildren || current[keys[i]]
	}

	const lastKey = keys[keys.length - 1]

	if(current[lastKey] && current[lastKey]){
		const removedNote = current[lastKey]
		delete current[lastKey] // Remove the note
		if (parent) {
			parent.childCount--
			if (parent.childCount === 0) {
				parent.showChildren = false
			}
		}
		return removedNote
	} else {
		return null
	}
}

// Helper function to add a note by path and open tree
const addNoteByPath = (notes, path, note, showChildren = true) => {
	const keys = path.split(".")
	let current = notes
	let parent = undefined

	for (let i = 0; i < keys.length - 1; i++) {
		// if note not loaded in, don't keep looking.
		if (!current || !current[keys[i]]) return
		parent = current[keys[i]]	
		current = current[keys[i]]?.noteChildren || current[keys[i]]
	}
	// should be an impossible condition given the guard at the top of the for loop,
	// but JUST IN CASE...
	if (!current) return

	const lastKey = keys[keys.length - 1]
	current[lastKey] = note // Add the note
	if (parent) {
		parent.showChildren = showChildren ? showChildren : parent.showChildren
		parent.childCount++
	}
}

export const deleteArticleNoteRelationFromIcon = createAsyncThunk(
	"workspaceNotes/deleteArticleNoteRelationFromIcon",
	async (note, {getState, rejectWithValue, dispatch}) => {
		const state = getState()
		const {workspaceId} = note
		if (!workspaceId) return rejectWithValue("Failed to remove article from note. Invalid Workspace ID")
		const {articleId} = state.workingArticleNoteRelation.byWorkspace[workspaceId]
		if(!articleId) return rejectWithValue("Failed to remove article from note. Invalid article ID")
		if(`${articleId}`.includes("attachment")){
			const attachmentId = articleId.split("/")[1]
			const attachment = {
				id: attachmentId,
				noteId: note.id
			}
			await dispatch(detachNoteFromAttachment({note, attachment}))
			return {
				note,
				articleId
			}
		}

		try {
			// this route returns the note path (originally consumed by refresh relay)
			await axios.delete(`/api/workspace/${workspaceId}/note/${note.id}/article/${articleId}`)
			// reload breadcrumbs if articleMap[articleId] !== null
			if (state.breadcrumbSlice?.byWorkspace[workspaceId].articleMap[articleId]) {
				dispatch(fetchArticleBreadcrumbs({article: {id: articleId}, note, workspaceId}))
			}
			return {
				note,
				articleId
			}
		} catch (error) {
			return rejectWithValue(error?.response?.message || `Failed to remove note ${note.id} from article using note icon`)
		}
	}
)

export const fetchTopLevelNotes = createAsyncThunk(
	"workspaceNotes/fetchTopLevelNotes",
	async (workspaceId, {getState, rejectWithValue}) => {
		let workspaceIdToFetch
		if(workspaceId) {
			workspaceIdToFetch = workspaceId
		} else { //TODO: remove this after refactor, as all dispatches should set workspaceId value
			const state = getState()
			workspaceIdToFetch = state.workspace?.primaryId
		}

		if (!workspaceIdToFetch) return {}

		try {
			const {data: topLevelNotes} = await axios
				.get(`/api/workspace/${workspaceIdToFetch}/note/0/children`)
			const flatNotes = noteArrayToFlatObject(topLevelNotes)
			const rootOrder = topLevelNotes.map(note => note.id)
			return { flatNotes, rootOrder, workspaceId }
		} catch (error) {
			return rejectWithValue(error?.response?.message || "Error fetching children")
		}

	}
)

export const deleteNote = createAsyncThunk(
	"workspaceNotes/deleteNote",
	async (noteArg, {getState, rejectWithValue, dispatch}) => {
		const {noteId, workspaceId} = noteArg
		const {workspaceNotes, breadcrumbSlice} = getState()
		let isTopLevelEmpty = false

		if (!workspaceId) return rejectWithValue("Failed to remove workspace from note. Invalid Workspace ID")

		try {
			const { data } = await axios.delete(`/api/workspace/${workspaceId}/note/${noteId}`)
			const path = `${data.path}`
			const relatedArticleIds = data.articles
			const unassignedArticles = data.unassignedArticles
			const parentPath = path.split(".").slice(0, -1).join(".")
			let originalParent
			// fetch unique count if parent path exists (not top level note)
			if (parentPath) {
				originalParent = getNoteByPath(workspaceNotes.byWorkspace[workspaceId].notes, parentPath)
				await dispatch(fetchUniqueCounts({id: originalParent.id, workspaceId}))
			}
			const breadcrumbs = breadcrumbSlice.byWorkspace[workspaceId]?.articleMap || {}
			for (const [key] of Object.entries(breadcrumbs)) {
				if(relatedArticleIds.includes(+key)) {
					// update breadcrumb for article
					// passing in a fake article object that only includes id
					await dispatch(fetchArticleBreadcrumbs({article: {id: +key}, workspaceId}))
				}
			}
			const isDeletedNoteRoot = path.split(".").length === 1
			if (isDeletedNoteRoot) {
				const topLevelCountAfterDeletion = Object.keys(workspaceNotes.byWorkspace[workspaceId].notes).length - 1
				isTopLevelEmpty = topLevelCountAfterDeletion === 0
			}
			// return path to remove from tree, related articles to update breadcrumbs, and unassigned articles to add to the unassigned list
			return {path, relatedArticleIds, unassignedArticles, isTopLevelEmpty, partialDeletedNoteNfo: {noteId, parentNoteId: isDeletedNoteRoot ? "root" : originalParent.id}, workspaceId}
		} catch (error) {
			console.error("failed to delete note in noteslice thunk: ", error)
			return rejectWithValue(error?.response?.message || `Error Deleting Note ${noteId}`)
		}
	}
)

export const fetchNewTopLevelNotesById = createAsyncThunk(
	"workspaceNotes/fetchNewTopLevelNotesById",
	async (noteRestrictionsPayload, { getState, rejectWithValue }) => {
		try {
			const {noteRestrictions: noteRestrictionsMap, workspaceId} = noteRestrictionsPayload
			const state = getState()
			const currentUserId = state.user?.value?.id

			const noteIds = Object.keys(noteRestrictionsMap)
			// Construct the base URL
			const baseUrl = `api/workspace/${workspaceId}/user/${currentUserId}/note`
			// Append the note IDs as query parameters
			const queryParams = noteIds.map(id => `noteIds=${id}`).join("&")
			const url = `${baseUrl}?${queryParams}`
			// Make the GET request
			const response = await axios.get(url)
			// Return the response data (or handle it as needed)
			return {incomingNotes: response.data, workspaceId}
		} catch (error) {
			// Handle or throw the error
			console.error("Error fetching notes", error)
			return rejectWithValue("Failed to fetch notes by id")
		}
	}
)

export const addNote = createAsyncThunk(
	"workspaceNotes/addNote",
	async ({note, content, workspaceId}, {getState, rejectWithValue}) => {
		const state = getState()
		const noteAction = state.noteAction?.byWorkspace[workspaceId].action
		const currentNotes = state.workspaceNotes?.byWorkspace[workspaceId].notes

		// check if adding sibling note
		const addingSiblingNote = noteAction === NOTE_ACTIONS.addSibling
		// if adding sibling, fetch the target note's parent and use that as parent note :OR: if adding child / root note, set target note as parent note
		const parentNote = addingSiblingNote ? getClosestParentNoteByPath(currentNotes, note.path, true) : note
		const addingRootNote = noteAction === NOTE_ACTIONS.addRoot
		try {
			// check if parent needs to open
			let incomingChildNotes = []
			if (!addingSiblingNote && !note.showChildren && note.childCount > Object.keys(note.noteChildren || {}).length) {
				const {data: children } = await axios.get(`/api/workspace/${workspaceId}/note/${note.id}/children`)
				incomingChildNotes = children
			}
			// create new note
			const {data: newNote} = await axios
				.post(`/api/workspace/${note.workspaceId}/note`, {
					parentId: addingRootNote ? null : parentNote.id,
					path: addingRootNote ? "" : parentNote.path,
					content: content,
					workspaceId: note.workspaceId,
				})
			// modify note order for positioning to ensure that siblings appear directly above the target note
			const newNoteObj = {...defaultNoteObjectProperties, ...newNote}
			if(addingSiblingNote) {
				newNoteObj.order = Math.max(note.order - 1) // Math.max forces -1 to 0
			} else {
				newNoteObj.order = 0
			}
			return {newNote: newNoteObj, parentNote, incomingChildNotes, invokingNote: note, addingSiblingNote, workspaceId}
		} catch (error) {
			console.error("Error adding note", error)
			return rejectWithValue(error?.response?.message || "Failed to create the note, please try again")
		}
	}
)

export const pasteCopy = createAsyncThunk(
	"workspaceNotes/copyNote",
	async ({pasteAsSibling, targetNote, pasteObject}, {getState, rejectWithValue, dispatch}) => {
		try {
			const state = getState()
			const currentUserId = state.user?.value?.id
			const targetWorkspace = targetNote.workspaceId
			const targetArticle = state.targetArticle?.byWorkspace[targetWorkspace]
			const {noteId, includeAttachments, noteToPaste} = pasteObject

			const { data } = await axios.post(
				`/api/workspace/${targetWorkspace}/user/${currentUserId}/note/${noteId}/paste`,
				{
					pasteAsSibling,
					targetNote,
					noteToPaste,
					includeAttachments
				}
			).catch((error) => {
				console.error("Error from request to paste copy of note: ", error)
				return rejectWithValue("Failed to paste note.")
			})
			dispatch(openToPath({path: data.note.path, note: data.note, insertTarget: true, targetNote, pasteAsSibling}))

			// if citations / attachments are being pasted, check their IDs to see if we need to update breadcrumbs
			if(includeAttachments && data.note.articleIdsOfAllChildren && data.note.articleIdsOfAllChildren.includes(targetArticle)) {
				await dispatch(fetchArticleBreadcrumbs({article: {id: targetArticle}, workspaceId: targetWorkspace}))
			}

			return data
		} catch (error) {
			console.error("Error pasting copy of note", error)
			return rejectWithValue(error.response?.data || "Failed to paste note")
		}
	}
)

export const moveNote = createAsyncThunk(
	"workspaceNotes/moveNote",
	async ( {movingNote, receivingNoteId, receivingPath, workspaceId}, { getState, rejectWithValue, dispatch }) => {
		const state = getState()

		if (!workspaceId) return {}
		const invokingNote = state.targetNote?.byWorkspace[workspaceId]

		try {
			// Check if new parent note is closed and needs to be opened with additional children
			const newParentNote = getNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, receivingPath)
			let incomingChildNotes = []
			if (newParentNote && !newParentNote.showChildren &&
				newParentNote.childCount > Object.keys(newParentNote.noteChildren || {}).length) {
				const {data: children } = await axios.get(`/api/workspace/${workspaceId}/note/${newParentNote.id || 0}/children`)
				incomingChildNotes = children
			}
			// Update note move on server
			const {data: newNote} = await axios.put(`/api/workspace/${workspaceId}/note/${movingNote.id}/location`, {
				receivingNoteId,
				movingNote,
			})
			// update unique counts for all parent notes in new location
			if (newParentNote) {
				// update receiving parent note hierarchy
				dispatch(fetchUniqueCounts({id: newParentNote.id, workspaceId}))
			}
			// update original parent note hierarchy
			const parentPathArray = movingNote.path.split(".")
			//only create a new parentPath if the note isn't moving away from root, otherwise the parentPath value will be a blank string
			const parentPath = parentPathArray.length > 1 ? movingNote.path.split(".").slice(0, -1).join(".") : movingNote.path
			const originalParent = getNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, parentPath)
			dispatch(fetchUniqueCounts({id: originalParent.id, workspaceId}))

			return {newNote, originalNote: movingNote, incomingChildNotes, receivingPath, originalNoteLocation: {parentId: movingNote.parentId === null ? null : originalParent.id}, invokingNote}
		} catch(error) {
			console.error("Error moving note", error)
			return rejectWithValue(error?.response?.message || "Error moving note")
		}
	}
)

/**
 * Takes a note's ID, looks up that note in the client-side tree, and then
 * fetches fresh unique count values for itself and for any/all children.
 *
 * @type {import("@reduxjs/toolkit").AsyncThunk}
 *
 * @param {Object} noteArg - the AsyncThunkPayloadCreator object
 * @param {number} noteArg.id - The note ID to fetch unique counts for.
 * @param {number} noteArg.workspaceId - The workspace containing the note.
 *
 * @returns {{ uniqueCounts: Array, workspaceId: number }} The unique count
 *   data for the note and its ancestors, plus the workspace ID for routing
 *   to the correct byWorkspace bucket.
 *
 * @rejects {string} When workspaceId is missing or the API call fails.
 */
export const fetchUniqueCounts = createAsyncThunk(
	"workspaceNotes/fetchUniqueCounts",
	async (noteArg, { rejectWithValue}) => {
		const {id: noteId, workspaceId} = noteArg
		if (!workspaceId) return rejectWithValue("Failed to fetch unique counts. Invalid WorkspaceId")
		try {
			const { data } = await axios.get(`/api/workspace/${workspaceId}/note/${noteId}/uniqueCount`)
			return { uniqueCounts: data, workspaceId }
		} catch (error) {
			console.error("Error fetching unique counts", error)
			return rejectWithValue(error?.response?.message || `Error fetching unique count for Note ${noteId}`)
		}
	}
)

export const fetchUniqueCountsForEachPath = createAsyncThunk(
	"workspaceNotes/fetchUniqueCountsForEachPath",
	async ({paths, workspaceId}, {getState, rejectWithValue, dispatch}) => {
		const state = getState()

		if (!workspaceId) return rejectWithValue("Failed to fetch unique counts. Invalid WorkspaceId")
		try {
			paths.forEach( path => {
				const targetNote = getClosestParentNoteByPath(state.workspaceNotes.byWorkspace[workspaceId].notes, path)
				if(targetNote) {
					dispatch(fetchUniqueCounts({id: targetNote.id, workspaceId}))
				}
			})
		} catch (error) {
			return rejectWithValue(error?.response?.message || `Error fetching unique count for some or all notes in path array ${paths}`)
		}
	}
)

export const openToPath = createAsyncThunk(
	"workspaceNotes/openToPath",
	async ({path, note, insertTarget = false, fromWebsocket = false, targetNote, pasteAsSibling}, { getState, rejectWithValue, dispatch }) => {
		try {
			const state = getState()
			const { workspaceId } = note
			if (!workspaceId) return rejectWithValue("Failed to open to Path -- No workspaceId")
			// Split the path to navigate through the nested structure
			const keys = `${path}`.split(".")

			// Reference to the current point in the notes object
			let allNotes =  cloneDeep(state.workspaceNotes.byWorkspace[workspaceId].notes)
			let current = allNotes
			let lastValidNote = undefined
			let isParentInMemory = false
			// is incoming note parent in the current state and is not parent note
			const closestParent = getClosestParentNoteByPath(allNotes, note.path)

			if ((closestParent?.id === note.parentId && note.parentId) || !note.parentId) {
				isParentInMemory = true
			}

			if (!fromWebsocket || (fromWebsocket && isParentInMemory)) {
				for (const key of keys) {
					lastValidNote = current[key]

					if (!lastValidNote && key == note.id && insertTarget) {
						// check if incoming note is top level
						// if incoming note is not top level, than update parent note count
						if (!(keys.length === 1)) {
							const targetNoteParent = getNoteByPath(allNotes, note.path.split(".").slice(0, -1).join("."))
							targetNoteParent.childCount++
						} 

						lastValidNote = {
							...defaultNoteObjectProperties,
							...note
						}
					}

					// If we still don't have a valid note, we can't traverse further
					if (!lastValidNote) {
						break
					}
	
					// If closed and children not loaded, then fetch children and set open
					if ((Object.keys(lastValidNote.noteChildren || {}).length !== lastValidNote.childCount && key != note.id) && !fromWebsocket) {
						let {data: children} = await axios.get(`/api/workspace/${workspaceId}/note/${lastValidNote.id}/children`)
						const flatNotes = noteArrayToFlatObject(children)
						dispatch(setLoadedChildNotesOrder({flatNotes, workspaceId}))
						current[key] = {
							...lastValidNote,
							noteChildren: flatNotes,
							showChildren: true
						}
					} else {
						current[key] = {
							...lastValidNote,
							showChildren: !fromWebsocket ? (key != note.id) : lastValidNote.showChildren
						}
					}
					current = current[key].noteChildren || current[key]
				}
				if (insertTarget && targetNote) {
					dispatch(pushNoteToOrder({note, targetNote, pasteAsSibling, workspaceId}))
				}
			}
			dispatch(fetchUniqueCounts({id: note.id, workspaceId}))
			return {allNotes, note, insertTarget, fromWebsocket, workspaceId}
		} catch (error) {
			console.error("error opening note", error)
			return rejectWithValue(error?.response?.message || `Error opening note tree to path ${path}`)
		}
	}
)


export const fetchChildren = createAsyncThunk(
	"workspaceNotes/fetchChildren",
	async ({note, workspaceId}, { dispatch, rejectWithValue }) => {

		if (!workspaceId) return rejectWithValue("No workspace ID included")
		// is showing, just close
		if (note.showChildren) {
			// close all children
			const clonedNote = cloneDeep(note)
			closeAllChildren(clonedNote.noteChildren)
			return {
				...clonedNote,
				showChildren: false
			}
		}
		try {
			// If closed and children not loaded, then fetch children and set open
			if (Object.keys(note.noteChildren || {}).length !== note.childCount) {
				const {data: children} = await axios.get(`/api/workspace/${workspaceId}/note/${note.id}/children`)
				const flatNotes = noteArrayToFlatObject(children)

				dispatch(setLoadedChildNotesOrder({flatNotes, workspaceId}))
				return {
					...note,
					noteChildren: flatNotes,
					showChildren: true,
					workspaceId
				}
			} else {
				// else just open the note!
				return {
					...note,
					showChildren: true,
					workspaceId
				}
			}
		} catch (error) {
			console.error("error fetching child", error)
			return rejectWithValue({message: "Error fetching children", noteId: note.id})
		}


	}
)

const defaultWorkspaceNoteTreeShape = {
	notes: {},
	loading: false,
	submittingNote: {},
	error: null,
	attachError: null,
	addNoteError: null,
	loadingChildren: []
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, defaultWorkspaceNoteTreeShape)

export const workspaceNoteSlice = createSlice({
	name: "workspaceNotes",
	initialState: {
		notes: {},
		loading: true,
		submittingNote: {},
		error: null,
		attachError: null,
		addNoteError: null,
		loadingChildren: [],
		byWorkspace: {},
	},
	reducers: {
		updateNoteContent: (state, action) => {
			const newNote = action.payload
			const workspace = ensureWorkspace(state, newNote.workspaceId)
			const noteToUpdate = getNoteByPath(workspace.notes, newNote.path)
			if (noteToUpdate) {
				noteToUpdate.content = newNote.content
			}
		},
		moveNoteInTree: (state, action) => {
			// START HERE, WHAT HAPPENS WHEN ORIGINAL NOTE IS NOT IN MEMORY
			// What happens when receiving path is not in memory?
			const {newNote, originalNote, incomingChildNotes, receivingPath, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)

			const receivingNote = getNoteByPath(workspace.notes, receivingPath)
			let originalNoteRemoved = removeNoteByPath(workspace.notes, originalNote.path)
			if (!originalNoteRemoved) {
				// update originalNoteRemoved if original note is not in redux
				originalNoteRemoved = newNote
			} else {
				// Update original note path
				originalNoteRemoved.path = newNote.path
			}
			// Update parent children if needed (only happens if parent was closed)
			if (incomingChildNotes.length > 0 && receivingNote) {
				const parentNote = getNoteByPath(workspace.notes, receivingPath)
				const flatNotes = noteArrayToFlatObject(incomingChildNotes)
				parentNote.noteChildren = flatNotes
			}
			// Update child paths, open and update child count
			updateNestedPaths(originalNoteRemoved.noteChildren, newNote.path)
			if (originalNoteRemoved) {
				// Step 2: Add the note to the new location
				addNoteByPath(workspace.notes, newNote.path, {
					...newNote,
					childCount: originalNote.childCount,
					noteChildren: originalNoteRemoved.noteChildren,
					showChildren: originalNoteRemoved.showChildren

				}, false)
			}
		},
		addNoteToTree: (state, action) => {
			const newNote = action.payload
			const workspace = ensureWorkspace(state, newNote.workspaceId)
			// if top level note then just add
			if (!newNote.parentId) {
				addNoteByPath(workspace.notes, newNote.path, {...defaultNoteObjectProperties, ...newNote})
			}
			// check if note is in path
			const closestNote = getClosestParentNoteByPath(workspace.notes, newNote.path)
			// if closestNote is parent then 
			if (newNote.parentId === closestNote.id) {
				// if parent is open, add note to parent
				if (closestNote.showChildren) {
					closestNote.noteChildren[newNote.id] = {...defaultNoteObjectProperties, ...newNote}
				} 
				closestNote.childCount = closestNote.childCount ? closestNote.childCount + 1 : 1
			}
		},
		deleteNoteFromTree: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload?.workspaceId)
			removeNoteByPath(workspace.notes, action.payload?.path)
		},
		setSubmittingNote: (state, action) => {
			const {note, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.submittingNote = note
		},
		clearAttachError: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.attachError = null
		},
		clearAddNoteError: (state, action) => {
			const {workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.addNoteError = null
			workspace.submittingNote = {}
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNewTopLevelNotesById.fulfilled, (state, action) => {
				const {incomingNotes, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				// Add new notes from incomingNotes to notes
				incomingNotes.forEach((note, i )=> {
					if (!Object.prototype.hasOwnProperty.call(workspace.notes, note.id.toString())) {
						workspace.notes[note.id] = {...note, order: i}
					}
				})
				// remove any note that is note in the incoming list
				let incomingIds = new Set(incomingNotes.map(note => note.id))
				// Iterate over the keys of noteEntries and delete entries not in incomingIds
				for (let id in workspace.notes) {
					if (!incomingIds.has(parseInt(id))) {
						delete workspace.notes[id]
					}
				}
			})
			.addCase(openToPath.fulfilled, (state, action) => {
				const {workspaceId, allNotes} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.notes = allNotes
			})
			.addCase(openToPath.rejected, (state, payload) => {
				const workspaceId = payload.meta.arg.note.workspaceId
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.loading = false
			})
			.addCase(deleteNote.fulfilled, (state, action) => {
				const { path, workspaceId } = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				removeNoteByPath(workspace.notes, path)
			})
			.addCase(fetchUniqueCounts.fulfilled, (state, action) => {
				const {uniqueCounts: listOfArticleNoteData, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				listOfArticleNoteData.forEach(note => {
					let targetNote
					const keys = note.path.split(".")
					keys.forEach( key => {
						if (!targetNote) {
							targetNote = workspace.notes[key]
						} else {
							targetNote = targetNote[key]
						}
						// target note might note be loaded in
						// ignore updating target note if not loaded in
						if (targetNote) {
							targetNote.attachmentNoteCount = note.attachmentNoteCount
							targetNote.articleNoteCount = note.articleNoteCount
							targetNote.uniqueCount = note.uniqueCount
							targetNote.attachmentCount = note.attachmentCount
							targetNote = targetNote.noteChildren
						}

					})
				})

			})
			.addCase(fetchUniqueCounts.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.attachError = action.payload
			})
			.addCase(addNote.pending, (state, action) => {
				const {note} = action.meta.arg
				const {workspaceId} = note
				workspaceNoteSlice.caseReducers.setSubmittingNote(state, {payload: {workspaceId, note}})
			})
			.addCase(addNote.fulfilled, (state, action) => {
				const {newNote, parentNote, incomingChildNotes, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)

				if (incomingChildNotes.length > 0) {
					const parentNoteState = getNoteByPath(workspace.notes, parentNote.path)
					parentNoteState.noteChildren = noteArrayToFlatObject(incomingChildNotes)
				}
				addNoteByPath(workspace.notes, newNote.path, newNote)
				workspace.submittingNote = {}
				workspace.loading = false
			})
			.addCase(addNote.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.addNoteError = action.payload
				workspace.submittingNote = {}
				workspace.loading = false
			})
			.addCase(moveNote.fulfilled, (state, action) => {
				const {newNote, originalNote, incomingChildNotes, receivingPath} = action.payload
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				const currentWorkspaceNotes = workspace.notes || state.notes // TODO: remove fall-back onto global val
				// Locate and remove the original note
				const originalNoteRemoved = removeNoteByPath(currentWorkspaceNotes, originalNote.path)
				// Update original note path
				originalNoteRemoved.path = newNote.path
				// Update parent children if needed (only happens if parent was closed)
				if (incomingChildNotes.length > 0) {
					const parentNote = getNoteByPath(currentWorkspaceNotes, receivingPath)
					const flatNotes = noteArrayToFlatObject(incomingChildNotes)
					parentNote.noteChildren = flatNotes
				}
				// Update child paths, open and update child count
				updateNestedPaths(originalNoteRemoved.noteChildren, newNote.path)
				if (originalNoteRemoved) {
					// Step 2: Add the note to the new location
					addNoteByPath(currentWorkspaceNotes, newNote.path, {
						...newNote,
						childCount: originalNote.childCount,
						noteChildren: originalNoteRemoved.noteChildren,
						showChildren: originalNoteRemoved.showChildren

					})
				}
			})
			.addCase(moveNote.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload
			})
			.addCase(fetchTopLevelNotes.pending, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.loading = true
				workspace.error = null
			})
			.addCase(fetchTopLevelNotes.fulfilled, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				const { flatNotes } = action.payload
				workspace.loading = false
				workspace.notes = flatNotes
			})
			.addCase(fetchTopLevelNotes.rejected, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.loading = false
				workspace.error = action.payload
			})
			.addCase(fetchChildren.pending, (state, action) => {
				// Add the note ID to the loadingChildren array if it's not already there
				const {note, workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				if (!workspace.loadingChildren.includes(note.id)) {
					workspace.loadingChildren.push(note.id)
				}
				workspace.childrenError = null
			})
			.addCase(fetchChildren.fulfilled, (state, action) => {
				// Update the note with its children
				const newNote = action.payload
				const {workspaceId} = newNote
				const workspace = ensureWorkspace(state, workspaceId)
				const notePath = newNote.path
				// remove id from loadingChildren array
				workspace.loadingChildren = workspace.loadingChildren.filter(id => id !== newNote.id)
				// Split the path to navigate through the nested structure
				const keys = notePath.split(".")
				// Reference to the current point in the notes object
				let current = workspace.notes
				keys.forEach((key, index) => {
					if (index === keys.length - 1) {
						// Last key, update the note here
						current[key] = newNote
					} else {
						// Navigate to the next level
						current = current[key]?.noteChildren || current[key]
					}
				})
			})
			.addCase(fetchChildren.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				// remove id from loadingChildren array
				const {message, noteId} = action.payload
				workspace.loadingChildren = workspace.loadingChildren.filter(id => id !== noteId)
				workspace.childrenError = message ? message : action.payload
			})
	}
})

export const { updateNoteContent, setSubmittingNote, clearAttachError, addNoteToTree, deleteNoteFromTree, moveNoteInTree, clearAddNoteError } = workspaceNoteSlice.actions

// Selector to get the notes object from the state
const selectNotes = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.notes ?? defaultWorkspaceNoteTreeShape.notes

// Modified selector to return an array of allowed and sorted notes
export const selectTopLevelNotes = (workspaceId) => createSelector(
	[
		(state) => selectNotes(state, workspaceId),
		(state) => selectAllowedNoteIds(state,workspaceId),
		(state) => selectIsLoading(state, workspaceId),
	],
	(notes, allowedNoteIds, isLoading) => {
		// short circuit if note restrictions are still loading
		if (isLoading) {
			return []
		}
		// sort filtered notes
		if (allowedNoteIds.length !== 0) {
			const filteredNotes = Object.values(notes).filter(note => allowedNoteIds.includes(note.id))
			return filteredNotes.sort((x, y) => x.order - y.order)
		}
		// Sort raw notes
		return Object.values(notes).sort((x, y) => x.order - y.order)
	}
)
// Selector for loading state
export const selectLoading = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.loading ?? defaultWorkspaceNoteTreeShape.loading

// Selector for loading children
export const selectLoadingChildren = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.loadingChildren ?? defaultWorkspaceNoteTreeShape.loadingChildren

// Selector for error state
export const selectError = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.error ?? defaultWorkspaceNoteTreeShape.error

// Selector for attach article error state
export const selectAttachError = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.attachError ?? defaultWorkspaceNoteTreeShape.attachError

// Selector for add note error state
export const selectAddNoteError = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.addNoteError ?? defaultWorkspaceNoteTreeShape.addNoteError

// Selector for submitting note
export const selectSubmittingNote = (state, workspaceId) => state.workspaceNotes.byWorkspace[workspaceId]?.submittingNote ?? defaultWorkspaceNoteTreeShape.submittingNote



export default workspaceNoteSlice.reducer